/**
 * The hand-off from something the user is already looking at — a news article,
 * or the home screen's quick start (a topic and maybe a platform, no article) —
 * to a brief.
 *
 * A news card navigates to `/generation` carrying the article in router state,
 * and the form grounds the run on it by sending `source_url`. Router state is
 * *untrusted input* — it survives history entries, it is writable from the
 * console, and a refresh replays whatever was last pushed — so nothing here
 * trusts its shape: `readGenerationPrefill` is a full runtime guard, the URL has
 * to clear `safeExternalUrl` before it can reach the request body, and a
 * platform or post type it does not recognise is dropped rather than repaired.
 */

import { PLATFORM_OPTIONS } from '../../lib/platforms';
import { safeExternalUrl } from '../../lib/url';
import type {
  CaptionLength,
  CaptionStyle,
  NewsItemRead,
  PictureVariety,
  PlatformName,
  PostType,
} from '../../types/api';
import { readCaptionLength, readCaptionStyle } from './captionOptions';
import { readPictureVariety } from './pictureStyles';

/** Where a prefill is navigated to. One place, so the card cannot drift. */
export const GENERATION_PATH = '/generation';

/** The topic field's cap on the form. A prefilled topic is trimmed to fit it. */
const TOPIC_MAX_LENGTH = 500;

/** Post types the form knows. Mirrors the `PostType` union so the guard can. */
const POST_TYPES: readonly PostType[] = ['single_image', 'carousel', 'text_only'];

/**
 * What travels into the form.
 *
 * `topic` seeds the topic textarea and is the one field every hand-off carries.
 * `source_url` is what the pipeline's research node fetches, when there is an
 * article; `title` and `source` are for display only, so the form can name the
 * article without refetching the feed. All three travel together or not at all:
 * the home screen's quick start sends none of them.
 *
 * `platform` and `post_type` are optional because a news item has neither: an
 * article is not published anywhere in particular. They exist for the callers
 * that do carry them — a planned post names both — and when present they set
 * the form's own fields. `caption_length` and `caption_style` follow the same
 * rule: set the brief's controls when a caller carries them, absent otherwise.
 * So do `picture_style` and `picture_variety`; the style is only checked for
 * being a string here, and against the registry by the form, which has it.
 */
export interface GenerationPrefill {
  topic: string;
  source_url?: string;
  title?: string;
  source?: string;
  platform?: PlatformName;
  post_type?: PostType;
  caption_length?: CaptionLength;
  caption_style?: CaptionStyle;
  picture_style?: string;
  picture_variety?: PictureVariety;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/** A platform the form offers, or undefined. An unknown name is not a hint. */
function readPlatform(value: unknown): PlatformName | undefined {
  if (!isString(value)) return undefined;
  return PLATFORM_OPTIONS.find((option) => option.value === value)?.value;
}

/** A post type the form offers, or undefined. Same rule as the platform. */
function readPostType(value: unknown): PostType | undefined {
  if (!isString(value)) return undefined;
  return POST_TYPES.find((postType) => postType === value);
}

/**
 * Cut to `limit` characters on a word boundary, with an ellipsis when anything
 * was dropped. A hard slice mid-word reads like a bug; this reads like a quote.
 */
function truncate(text: string, limit: number): string {
  if (text.length <= limit) return text;
  // One character of the budget belongs to the ellipsis.
  const cut = text.slice(0, limit - 1);
  const lastSpace = cut.lastIndexOf(' ');
  // A single unbroken run of `limit` characters has no boundary to fall back
  // on, so the hard cut stands rather than emptying the field.
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * The topic an article makes: its headline, a blank line, then the feed's
 * snippet when there is one.
 *
 * This is what the user sees in the Topic box, and it is theirs to edit — the
 * article itself is fetched by the pipeline off `source_url`, so this text is
 * the *angle*, not the evidence. Pure and total: same item, same string.
 */
export function articleTopic(item: Pick<NewsItemRead, 'title' | 'snippet'>): string {
  const title = item.title.trim();
  const snippet = item.snippet.trim();
  const topic = snippet ? `${title}\n\n${snippet}` : title;
  return truncate(topic, TOPIC_MAX_LENGTH);
}

/**
 * A prefill out of `useLocation().state`, or null when the state is anything
 * else — a plain navigation, a stale entry, a hand-written object.
 *
 * Two shapes pass. An **article** hand-off: `source_url`, `topic`, `title` and
 * `source` all strings and the URL a real http(s) address — a partial match is
 * not repaired, it is rejected, because half an article is not something to
 * ground a post on. A **topic** hand-off: no `source_url` at all and a
 * non-empty `topic`. The optional fields are dropped individually when they
 * name nothing the form offers.
 */
export function readGenerationPrefill(state: unknown): GenerationPrefill | null {
  if (typeof state !== 'object' || state === null) return null;

  const {
    source_url,
    topic,
    title,
    source,
    platform,
    post_type,
    caption_length,
    caption_style,
    picture_style,
    picture_variety,
  } = state as Record<string, unknown>;
  if (!isString(topic)) return null;

  let prefill: GenerationPrefill;
  if (source_url === undefined) {
    // The quick start: the words alone, no article behind them.
    const trimmed = topic.trim();
    if (!trimmed) return null;
    prefill = { topic: truncate(trimmed, TOPIC_MAX_LENGTH) };
  } else {
    if (!isString(source_url) || !isString(title) || !isString(source)) return null;
    const safeUrl = safeExternalUrl(source_url);
    if (safeUrl === null) return null;
    prefill = {
      source_url: safeUrl,
      // The topic goes into a `maxLength`-capped textarea, so it is cut here
      // rather than silently refused by the field.
      topic: truncate(topic.trim(), TOPIC_MAX_LENGTH),
      title: title.trim(),
      source: source.trim(),
    };
  }

  const knownPlatform = readPlatform(platform);
  if (knownPlatform) prefill.platform = knownPlatform;
  const knownPostType = readPostType(post_type);
  if (knownPostType) prefill.post_type = knownPostType;
  const knownLength = readCaptionLength(caption_length);
  if (knownLength) prefill.caption_length = knownLength;
  const knownStyle = readCaptionStyle(caption_style);
  if (knownStyle) prefill.caption_style = knownStyle;
  if (isString(picture_style) && picture_style.trim()) prefill.picture_style = picture_style.trim();
  const knownVariety = readPictureVariety(picture_variety);
  if (knownVariety) prefill.picture_variety = knownVariety;

  return prefill;
}
